/**
 * Pure layout + policy for the desktop side-rail card stack.
 *
 * The right rail can show several collapsible cards (Opportunities / page rail,
 * Documents, Kai insight, Kai chat, Notes). This module is the single source of
 * truth for how those cards share the rail's fixed height:
 *
 * - The rail height never changes; every expanded card shares the viewport
 *   equally (flex-grow). Scroll happens inside card bodies, not on the rail.
 * - When {@link countPresentPanels} is at most {@link EXPAND_ALL_PRESENT_PANEL_LIMIT},
 *   every present card may be expanded at once.
 * - With more than that many cards on the rail, at most
 *   {@link MAX_EXPANDED_WHEN_BUSY_RAIL} may be expanded in parallel.
 * - When opening one more would exceed the cap, the bottom-most expanded card
 *   (in {@link RAIL_PANEL_ORDER}) is collapsed first, never the focused one.
 *
 * Everything here is a pure function so it can be unit-tested and reused by both
 * the context (state enforcement) and the view (rendering).
 */

/** Rail cards, addressed by a stable id. */
export type RailStackPanelId =
  | "page"
  | "opportunity"
  | "lookup"
  | "blDocument"
  | "blGroup"
  | "assignedRate"
  | "matchedSchedule"
  | "documents"
  | "insight"
  | "kai"
  | "notes";

/**
 * Booking-detail "primary content" cards. These were historically squeezed into
 * the single generic {@link RailStackPanelId} `page` slot (one at a time), which
 * forced them to be mutually exclusive. They are now first-class stack cards so
 * they coexist with each other and the secondary booking cards.
 */
export type BookingRailSlotId = "opportunity" | "lookup" | "blDocument";

/**
 * Visual order of the rail cards, top to bottom. This drives both rendering and
 * the "collapse from the bottom first" policy.
 */
export const RAIL_PANEL_ORDER: readonly RailStackPanelId[] = [
  "page",
  "opportunity",
  "lookup",
  "blDocument",
  "blGroup",
  "assignedRate",
  "matchedSchedule",
  "documents",
  "insight",
  "kai",
  "notes",
];

/** With at most this many cards on the rail, all may be expanded together. */
export const EXPAND_ALL_PRESENT_PANEL_LIMIT = 3;

/** When more than {@link EXPAND_ALL_PRESENT_PANEL_LIMIT} cards are present. */
export const MAX_EXPANDED_WHEN_BUSY_RAIL = 2;

/** @deprecated Use {@link maxExpandedRailPanels} — kept for tests defaulting to busy rail. */
export const MAX_EXPANDED_RAIL_PANELS = MAX_EXPANDED_WHEN_BUSY_RAIL;

/** A boolean flag per rail card. */
export type RailPanelFlags = Record<RailStackPanelId, boolean>;

/** How the rail lays out its cards for the current context. */
export type RailStackMode =
  /** A single overlay panel (notifications / rate-line detail) owns the rail. */
  | "monopolize"
  /** Desktop stacked cards sharing the fixed rail height. */
  | "stacked"
  /** Mobile drawer — cards grow with content and the drawer scrolls. */
  | "mobileFlow";

/** Per-card rendering decisions derived from the current state. */
export interface RailPanelLayout {
  /** The card exists in this context and should render at all. */
  present: boolean;
  /** The card's body is open (header + content visible). */
  bodyOpen: boolean;
  /** Desktop only: the open body flexes to share the rail height equally. */
  fill: boolean;
  /** This card is the focused / protected one. */
  focused: boolean;
}

/** Everything the layout needs, already resolved from context + props. */
export interface RailStackInput {
  /** Which cards exist in this rail context. */
  present: RailPanelFlags;
  /** Which cards the user has opened (independent of `present`). */
  userOpen: RailPanelFlags;
  /** The focused / protected card, or `null` (e.g. an overlay is focused). */
  focused: RailStackPanelId | null;
  /** An overlay (notifications / rate line) currently owns the rail. */
  monopolize: boolean;
  /** Mobile drawer flow layout instead of the fixed-height desktop stack. */
  stack: boolean;
}

/** The full computed layout for one render. */
export interface RailStackLayout {
  mode: RailStackMode;
  panels: Record<RailStackPanelId, RailPanelLayout>;
  /** Count of cards whose body is open in the stacked desktop layout. */
  openCount: number;
}

function emptyFlags(): RailPanelFlags {
  return {
    page: false,
    opportunity: false,
    lookup: false,
    blDocument: false,
    blGroup: false,
    assignedRate: false,
    matchedSchedule: false,
    documents: false,
    insight: false,
    kai: false,
    notes: false,
  };
}

/** How many stack cards exist in this context. */
export function countPresentPanels(
  present: RailPanelFlags,
  order: readonly RailStackPanelId[] = RAIL_PANEL_ORDER,
): number {
  return order.filter((id) => present[id]).length;
}

/** Parallel expand cap from how many cards are on the rail. */
export function maxExpandedRailPanels(
  present: RailPanelFlags,
  order: readonly RailStackPanelId[] = RAIL_PANEL_ORDER,
): number {
  const count = countPresentPanels(present, order);
  if (count <= EXPAND_ALL_PRESENT_PANEL_LIMIT) return count;
  return MAX_EXPANDED_WHEN_BUSY_RAIL;
}

/** Cards that are both present and open, in top-to-bottom order. */
export function openPresentPanels(
  open: RailPanelFlags,
  present: RailPanelFlags,
  order: readonly RailStackPanelId[] = RAIL_PANEL_ORDER,
): RailStackPanelId[] {
  return order.filter((id) => open[id] && present[id]);
}

/** The bottom-most present + open card, optionally excluding one id. */
export function bottomMostExpanded(
  open: RailPanelFlags,
  present: RailPanelFlags,
  excludeId: RailStackPanelId | null = null,
  order: readonly RailStackPanelId[] = RAIL_PANEL_ORDER,
): RailStackPanelId | null {
  for (let i = order.length - 1; i >= 0; i--) {
    const id = order[i];
    if (id === excludeId) continue;
    if (open[id] && present[id]) return id;
  }
  return null;
}

/**
 * Enforce the "at most N expanded" rule. Returns a new open-flag record with the
 * bottom-most present + open cards collapsed until only `max` remain. The
 * `protectedId` (typically the card just opened / focused) is never collapsed.
 */
export function enforceMaxExpanded(
  open: RailPanelFlags,
  present: RailPanelFlags,
  protectedId: RailStackPanelId | null = null,
  max: number = MAX_EXPANDED_RAIL_PANELS,
  order: readonly RailStackPanelId[] = RAIL_PANEL_ORDER,
): RailPanelFlags {
  const next: RailPanelFlags = { ...open };
  let overflow = openPresentPanels(next, present, order).length - max;
  if (overflow <= 0) return next;

  for (let i = order.length - 1; i >= 0 && overflow > 0; i--) {
    const id = order[i];
    if (!next[id] || !present[id]) continue;
    if (id === protectedId) continue;
    next[id] = false;
    overflow--;
  }
  return next;
}

/**
 * When no present card is expanded, open Notes if it is on the rail. Keeps the
 * stack from ending up entirely collapsed (Notes is the default expanded card).
 */
export function ensureDefaultExpandedWhenAllCollapsed(
  open: RailPanelFlags,
  present: RailPanelFlags,
  order: readonly RailStackPanelId[] = RAIL_PANEL_ORDER,
): RailPanelFlags {
  if (!present.notes) return open;
  if (openPresentPanels(open, present, order).length > 0) return open;
  return { ...open, notes: true };
}

/**
 * When parallel expand capacity remains, open Notes so the rail does not sit
 * half-empty. Skipped if the user explicitly collapsed Notes this session.
 */
export function ensureNotesExpandedWhenCapacity(
  open: RailPanelFlags,
  present: RailPanelFlags,
  max: number,
  userDismissedNotes: boolean,
  order: readonly RailStackPanelId[] = RAIL_PANEL_ORDER,
): RailPanelFlags {
  if (userDismissedNotes || !present.notes || open.notes) return open;
  if (openPresentPanels(open, present, order).length >= max) return open;
  return { ...open, notes: true };
}

/** Bottom-most present + open stack card, optionally skipping one id (e.g. Notes). */
export function bottomMostOpenStackPanel(
  layout: RailStackLayout,
  excludeId: RailStackPanelId | null = "notes",
  order: readonly RailStackPanelId[] = RAIL_PANEL_ORDER,
): RailStackPanelId | null {
  for (let i = order.length - 1; i >= 0; i--) {
    const id = order[i];
    if (id === excludeId) continue;
    const panel = layout.panels[id];
    if (panel.present && panel.bodyOpen) return id;
  }
  return null;
}

/**
 * Desktop stack: expanded cards share the fixed rail viewport equally (`flex-grow`).
 * Collapsed cards stay header-height only; the outer rail never scrolls.
 */
export function railStackShareGrow(
  layout: RailStackLayout,
  stack: boolean,
  id: RailStackPanelId,
): boolean {
  if (!stack) return false;
  const panel = layout.panels[id];
  return panel.present && panel.bodyOpen;
}

/** Count of present + expanded cards in desktop stack mode. */
export function expandedStackPanelCount(
  layout: RailStackLayout,
  order: readonly RailStackPanelId[] = RAIL_PANEL_ORDER,
): number {
  return order.filter(
    (id) => layout.panels[id].present && layout.panels[id].bodyOpen,
  ).length;
}

/**
 * Pick the card that should receive focus, preferring `preferred` when it is
 * present + open, otherwise the top-most present + open card. Returns `null` when
 * nothing is open.
 */
export function nextFocusPanel(
  open: RailPanelFlags,
  present: RailPanelFlags,
  preferred: RailStackPanelId | null = null,
  order: readonly RailStackPanelId[] = RAIL_PANEL_ORDER,
): RailStackPanelId | null {
  if (preferred && open[preferred] && present[preferred]) return preferred;
  for (const id of order) {
    if (open[id] && present[id]) return id;
  }
  return null;
}

/**
 * Compute the full rendering layout for one frame. Assumes `userOpen` has already
 * been capped by {@link enforceMaxExpanded} in state, but stays correct even if
 * it has not (extra open cards simply share height).
 */
export function computeRailStackLayout(input: RailStackInput): RailStackLayout {
  const mode: RailStackMode = input.stack
    ? "mobileFlow"
    : input.monopolize
      ? "monopolize"
      : "stacked";

  const panels = {} as Record<RailStackPanelId, RailPanelLayout>;
  let openCount = 0;

  for (const id of RAIL_PANEL_ORDER) {
    const present = input.present[id];
    const bodyOpen = present && input.userOpen[id] && mode !== "monopolize";
    if (bodyOpen && mode === "stacked") openCount++;
    panels[id] = {
      present,
      bodyOpen,
      fill: mode === "stacked" && bodyOpen,
      focused: input.focused === id && bodyOpen,
    };
  }

  return { mode, panels, openCount };
}

/** Convenience: a fresh all-false flag record. */
export function railPanelFlags(
  overrides: Partial<RailPanelFlags> = {},
): RailPanelFlags {
  return { ...emptyFlags(), ...overrides };
}
